// Shared "Assign & send" step for report-creation dialogs (Inspections/CDM/SHE/QA/
// Scaffold, CPP, TMP, BSC). Lets the creator pick client contacts + manual emails +
// an optional message up front — the finished report PDF then auto-emails to them
// once it's completed, with zero separate manual "email this report" step needed.
import { useEffect, useState } from "react";
import { X, Plus, Mail } from "lucide-react";
import { api } from "@/lib/api";

export function useAssignRecipients(clientId) {
    const [contacts, setContacts] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [manualEmails, setManualEmails] = useState([]);
    const [manualInput, setManualInput] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (!clientId) { setContacts([]); return; }
        api.get("/contacts/picklist", { params: { client_id: clientId } }).then((r) => setContacts(r.data)).catch(() => setContacts([]));
    }, [clientId]);

    const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const addManual = () => {
        const email = manualInput.trim();
        if (email && /\S+@\S+\.\S+/.test(email) && !manualEmails.includes(email)) {
            setManualEmails((prev) => [...prev, email]);
            setManualInput("");
        }
    };
    const removeManual = (email) => setManualEmails((prev) => prev.filter((e) => e !== email));

    const recipients = [
        ...contacts.filter((c) => selectedIds.includes(c.id)).map((c) => ({ contact_id: c.id, name: c.name, email: c.email })),
        ...manualEmails.map((email) => ({ email })),
    ];

    return { contacts, selectedIds, toggle, manualEmails, manualInput, setManualInput, addManual, removeManual, message, setMessage, recipients };
}

export function AssignRecipientsStep({ state, testIdPrefix = "assign", description }) {
    const { contacts, selectedIds, toggle, manualEmails, manualInput, setManualInput, addManual, removeManual, message, setMessage } = state;
    return (
        <div className="rounded-lg border p-3 space-y-2.5" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid={`${testIdPrefix}-section`}>
            <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#6F6A64" }}>Assign & send (optional)</span>
            </div>
            <p className="text-[11px]" style={{ color: "#9A968F" }}>
                {description || "Choose who should automatically receive this report by email once it's completed — no separate send step needed afterwards."}
            </p>
            {contacts.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 border rounded-lg p-2 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    {contacts.map((c) => (
                        <label key={c.id} data-testid={`${testIdPrefix}-contact-${c.id}`} className="flex items-center gap-2 text-[12px] cursor-pointer py-0.5">
                            <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggle(c.id)} />
                            <span className="font-semibold text-[#2D2D2D]">{c.name}</span>
                            <span style={{ color: "#9A968F" }}>{c.email}</span>
                        </label>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-2">
                <input data-testid={`${testIdPrefix}-manual-email`} value={manualInput} onChange={(e) => setManualInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
                    placeholder="Add an email manually" type="email"
                    className="flex-1 px-2.5 py-1.5 border rounded-lg text-[12px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                <button type="button" data-testid={`${testIdPrefix}-manual-add`} onClick={addManual}
                    className="w-7 h-7 rounded-lg border grid place-items-center bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <Plus className="w-3.5 h-3.5" />
                </button>
            </div>
            {manualEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {manualEmails.map((email) => (
                        <span key={email} data-testid={`${testIdPrefix}-manual-chip-${email}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px]" style={{ background: "#F0EDE8", color: "#2D2D2D" }}>
                            {email}
                            <button type="button" onClick={() => removeManual(email)}><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            <textarea data-testid={`${testIdPrefix}-message`} value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional message to include in the email" rows={2}
                className="w-full px-2.5 py-1.5 border rounded-lg text-[12px] outline-none resize-none" style={{ borderColor: "#E5E0DA" }} />
        </div>
    );
}
